import { PlusOne, Search, SearchOff } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import CachedIcon from "@mui/icons-material/Cached";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { assignTender, listTenders, removeTender } from "../../api/procedure";
import { useTranslationHook } from "../../hooks";
import { Account } from "../../types/model/account";
import { Procedure } from "../../types/model/procedure";
import { formatDate } from "../../utils/formatDate";
import ProcedureTenderSelector from "./ProcedureTenderSelector";

interface TenderListProps {
  accountId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  procedure: Procedure;
  readOnly: boolean;
}

const TenderList = ({
  accountId,
  projectId,
  activityId,
  procedureId,
  procedure,
  readOnly
}: TenderListProps) => {
  const { t } = useTranslationHook();

  const [tenderList, setTenderList] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  const fetchTenderList = async () => {
    setLoading(true);
    try {
      const response = await listTenders(projectId, activityId, procedureId);
      setTenderList(response);
      enqueueSnackbar(t("tender.tenders-loaded"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("error.tenders-loaded-error"), { variant: "error" });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tenderId: number) => {
    try {
      await removeTender(projectId, activityId, procedureId, tenderId);
      enqueueSnackbar(t("tender.delete-success"), { variant: "success" });
      fetchTenderList();
    } catch (error) {
      enqueueSnackbar(t("error.tenders-deleted-error"), { variant: "error" });
      console.error(error);
    }
  };

  const handleSelectTender = async (tenderId: number) => {
    try {
      await assignTender(projectId, activityId, procedureId, tenderId);
      enqueueSnackbar(t("tender.add-success"), { variant: "success" });
      fetchTenderList();
    } catch (error) {
      enqueueSnackbar(t("error.tender-add-error"), { variant: "error" });
      console.error(error);
    } finally {
      setIsSelectorOpen(false);
    }
  };

  useEffect(() => {
    fetchTenderList();
  }, [accountId]);

  const filteredTenders = tenderList.filter((tender) =>
    tender.name.toLowerCase().includes(search.toLowerCase())
  );

  const visibleRows = filteredTenders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>

          <Toolbar>
            <Typography sx={{ flex: "1 1 100%" }} variant="h5">
              {t("tender.tenders")}
            </Typography>

            <Tooltip title={t("generic.refresh")}>
              <IconButton onClick={fetchTenderList}>
                <CachedIcon />
              </IconButton>
            </Tooltip>

            {!readOnly && (
              <Tooltip title={t("generic.add")}>
                <IconButton
                  onClick={() => setIsSelectorOpen(true)}
                  disabled={procedure.canceled || procedure.closed}
                >
                  <PlusOne />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>

          <TextField
            size="small"
            placeholder={t("generic.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />

          <TableContainer sx={{ flex: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.accounts.name")}</TableCell>
                  <TableCell>{t("admin.accounts.created-on")}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">{t("generic.actions")}</TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                        <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                        <Typography variant="h6">
                          {t("tender.no-tenders")}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((tender) => (
                    <TableRow hover key={tender.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <BusinessIcon sx={{ color: "text.secondary" }} />
                          {tender.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDate(new Date(tender.createdOn).toISOString())}
                      </TableCell>
                      {!readOnly && (
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleDelete(tender.id)}
                            color="error"
                            size="small"
                            disabled={procedure.canceled || procedure.closed}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filteredTenders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number.parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Box>

      <ProcedureTenderSelector
        open={isSelectorOpen}
        accountId={accountId}
        currentTenders={tenderList}
        onSelect={handleSelectTender}
        onCancel={() => setIsSelectorOpen(false)}
      />
    </>
  );
};

export default TenderList;